import { useState, useEffect, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Mail, Trash2, Play, Eye, RefreshCw,
  FileBarChart, Users, Wallet, AlertCircle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const REPORT_TYPE_META = {
  sales_digest:      { icon: FileBarChart, label: "Sales Digest", color: "text-blue-600 bg-blue-50 border-blue-200" },
  collection_digest: { icon: Wallet,       label: "Collection Digest", color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  leaderboard:       { icon: Users,        label: "Sales Leaderboard", color: "text-amber-600 bg-amber-50 border-amber-200" },
  pending_approvals: { icon: AlertCircle,  label: "Pending Approvals", color: "text-red-600 bg-red-50 border-red-200" },
};

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ReportSchedulingPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", report_type: "sales_digest", frequency: "daily",
    day_of_week: 0, day_of_month: 1, hour: 8, minute: 0,
    recipients: "",
  });
  const [viewRun, setViewRun] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        authFetch(`${API}/report-schedules`).then((x) => x.json()),
        authFetch(`${API}/report-schedules/runs?limit=20`).then((x) => x.json()),
      ]);
      if (s.success) setSchedules(s.data);
      if (r.success) setRuns(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createSchedule = async () => {
    if (!form.name.trim() || !form.recipients.trim()) {
      toast.error("Name and at least one recipient required");
      return;
    }
    const emails = form.recipients.split(",").map((e) => e.trim()).filter(Boolean);
    const body = {
      name: form.name, report_type: form.report_type, frequency: form.frequency,
      hour: parseInt(form.hour), minute: parseInt(form.minute), recipients: emails,
    };
    if (form.frequency === "weekly") body.day_of_week = parseInt(form.day_of_week);
    if (form.frequency === "monthly") body.day_of_month = parseInt(form.day_of_month);
    try {
      const res = await authFetch(`${API}/report-schedules`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.success) {
        toast.success("Schedule created");
        setDialogOpen(false);
        setForm({ ...form, name: "", recipients: "" });
        fetchAll();
      } else {
        toast.error(d.detail || "Failed");
      }
    } catch { toast.error("Network error"); }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    const res = await authFetch(`${API}/report-schedules/${id}`, { method: "DELETE" });
    const d = await res.json();
    if (d.success) { toast.success("Deleted"); fetchAll(); }
    else toast.error(d.detail || "Failed");
  };

  const runNow = async (id) => {
    const res = await authFetch(`${API}/report-schedules/${id}/run-now`, { method: "POST" });
    const d = await res.json();
    if (d.success) {
      toast.success("Digest generated — delivery logged");
      setViewRun(d.data);
      fetchAll();
    } else toast.error(d.detail || "Failed");
  };

  return (
    <div className="space-y-6" data-testid="report-scheduling-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora'] flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-[#8B1A1A]" /> Report Scheduling
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Set up automated daily / weekly / monthly digests for sales, collections, leaderboards, and pending approvals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-schedules"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
          <Button onClick={() => setDialogOpen(true)} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="new-schedule-btn">
            <Plus className="w-4 h-4 mr-2" />New Schedule
          </Button>
        </div>
      </div>

      <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-900" data-testid="email-note">
        <strong>Heads up:</strong> Actual email delivery is pending SendGrid/Resend/SMTP wiring.
        Right now "Run Now" generates the digest payload and logs it to <code className="text-xs bg-amber-100 px-1 rounded">digest_runs</code> so you can verify correctness.
        Once an email provider is configured, runs will be emailed automatically at their scheduled times.
      </Card>

      {/* Schedules List */}
      <Card className="p-5" data-testid="schedules-card">
        <h3 className="font-semibold text-sm mb-3">Active Schedules</h3>
        {loading ? (
          <div className="py-10 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No schedules yet. Click "New Schedule" to create one.</p>
        ) : (
          <div className="space-y-2">
            {schedules.map((s) => {
              const meta = REPORT_TYPE_META[s.report_type] || REPORT_TYPE_META.sales_digest;
              const Icon = meta.icon;
              const timeStr = `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;
              const freqStr = s.frequency === "daily" ? "Daily" :
                              s.frequency === "weekly" ? `Weekly · ${DOW[s.day_of_week || 0]}` :
                                                          `Monthly · Day ${s.day_of_month || 1}`;
              return (
                <div key={s.schedule_id} className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg" data-testid={`sched-${s.schedule_id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.color} border`}><Icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      {meta.label} · {freqStr} at {timeStr} · {s.recipients?.length || 0} recipient{s.recipients?.length !== 1 ? "s" : ""}
                    </p>
                    {s.last_run_at && <p className="text-[10px] text-slate-400 mt-0.5">Last run: {new Date(s.last_run_at).toLocaleString()}</p>}
                  </div>
                  <Badge variant="outline" className={s.active ? "text-emerald-700 border-emerald-300 bg-emerald-50 text-xs" : "text-slate-600 text-xs"}>
                    {s.active ? "Active" : "Paused"}
                  </Badge>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => runNow(s.schedule_id)} data-testid={`run-now-${s.schedule_id}`} title="Run now">
                    <Play className="w-3.5 h-3.5 text-emerald-600" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => deleteSchedule(s.schedule_id)} data-testid={`delete-${s.schedule_id}`} title="Delete">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Recent Runs */}
      <Card className="p-5" data-testid="runs-card">
        <h3 className="font-semibold text-sm mb-3">Recent Digest Runs</h3>
        {runs.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No runs yet.</p>
        ) : (
          <div className="space-y-1.5">
            {runs.map((r) => (
              <div key={r.run_id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded text-sm" data-testid={`run-${r.run_id}`}>
                <Mail className="w-3.5 h-3.5 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <p className="truncate"><strong>{r.schedule_name}</strong> · {REPORT_TYPE_META[r.report_type]?.label || r.report_type}</p>
                  <p className="text-[10px] text-slate-400">{new Date(r.ran_at).toLocaleString()} · {r.recipients?.length || 0} recipients · {r.delivery_status}</p>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setViewRun(r)} data-testid={`view-${r.run_id}`}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="schedule-dialog">
          <DialogHeader><DialogTitle>New Report Schedule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Daily Sales Morning Digest" data-testid="form-name" />
            </div>
            <div>
              <label className="text-xs font-medium">Report Type</label>
              <Select value={form.report_type} onValueChange={(v) => setForm({ ...form, report_type: v })}>
                <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(REPORT_TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Frequency</label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="form-freq"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === "weekly" && (
                <div>
                  <label className="text-xs font-medium">Day of Week</label>
                  <Select value={String(form.day_of_week)} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.frequency === "monthly" && (
                <div>
                  <label className="text-xs font-medium">Day of Month</label>
                  <Input type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} />
                </div>
              )}
              <div>
                <label className="text-xs font-medium">Hour (24h)</label>
                <Input type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} data-testid="form-hour" />
              </div>
              <div>
                <label className="text-xs font-medium">Minute</label>
                <Input type="number" min={0} max={59} value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Recipients (comma-separated emails)</label>
              <Input value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="admin@avicement.com, sales@avicement.com" data-testid="form-recipients" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={createSchedule} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="form-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Run Dialog */}
      <Dialog open={!!viewRun} onOpenChange={(o) => { if (!o) setViewRun(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="run-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewRun?.schedule_name} · Run on {viewRun && new Date(viewRun.ran_at).toLocaleString()}</DialogTitle>
          </DialogHeader>
          {viewRun && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded">
                {Object.entries(viewRun.digest?.summary || {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">{k.replace(/_/g, " ")}</p>
                    <p className="font-mono font-semibold">{typeof v === "number" && k.includes("paise") ? `₹${(v / 100).toLocaleString("en-IN")}` : v}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 italic">{viewRun.delivery_note}</p>
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600">Raw digest (JSON)</summary>
                <pre className="mt-2 p-2 bg-slate-900 text-slate-100 rounded overflow-x-auto text-[11px]">{JSON.stringify(viewRun.digest, null, 2)}</pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportSchedulingPage;
